/* eslint-disable class-methods-use-this */

import {isString} from 'lodash';

import {FeatureFlagsEnum} from '../../../../store/featureFlags/featureFlagsTypes';
import {AtomParamTypesEnum, ClientAtom, UninferredAtom} from '../expressionAtomModel';
import {ExpressionFeatureFlagsType} from '../expressionFeatureFlags';
import {buildExpressionName} from '../expressionModel';
import {buildParameter} from '../expressionParamHelpers';
import type {ExpressionsContext} from '../expressionsContext';
import {ExpressionDependencies} from '../expressionState';
import {
  CircleResult,
  InferredType,
  Method,
  MethodCategoriesEnum,
  StaticallyTypedMethod,
  StringResult,
  TeammateListResult,
  TransformerIdsEnum,
  UnknownResult,
  UrlResult,
} from './internal';

interface ValueFactoryOptions {
  format?: string;
  featureFlag?: ExpressionFeatureFlagsType;
}

class ValueFactory extends StaticallyTypedMethod {
  private format?: string;

  constructor(
    private paramType: AtomParamTypesEnum,
    inferredType: InferredType,
    options: ValueFactoryOptions = {},
  ) {
    const {featureFlag, format} = options;
    super(MethodCategoriesEnum.CREATE, inferredType, {featureFlag});

    this.format = format;
  }

  getParameters() {
    return [buildParameter('value', this.paramType, this.format)];
  }
}

export class UsePreviousExpression extends Method {
  constructor() {
    super(MethodCategoriesEnum.CREATE);
  }

  inferType(atom: UninferredAtom, context: ExpressionsContext) {
    const {expression: expressionId} = atom.params;
    if (!isString(expressionId)) {
      return UnknownResult;
    }

    const selectedExpression = context.expressions.find((expression) => expression.id === expressionId);

    return selectedExpression ? selectedExpression.inferredType : UnknownResult;
  }

  getDefaultName(atom: ClientAtom, context: ExpressionsContext, dependencies: ExpressionDependencies) {
    const {expression: expressionId} = atom.params;
    if (!isString(expressionId)) {
      return super.getDefaultName(atom, context, dependencies);
    }

    const selectedExpression = context.expressions.find((expression) => expression.id === expressionId);

    return selectedExpression
      ? buildExpressionName(selectedExpression)
      : super.getDefaultName(atom, context, dependencies);
  }

  getParameters() {
    return [buildParameter('expression', AtomParamTypesEnum.NUMBER, 'expression')];
  }

  getAtomDefaultParameters(context?: ExpressionsContext) {
    const firstExpression = context?.expressions[0];

    return {expression: firstExpression && firstExpression.id};
  }
}

export const baseFactoryMethods: Partial<Record<TransformerIdsEnum, Method>> = {
  create_string: new ValueFactory(AtomParamTypesEnum.STRING, StringResult),
  create_url: new ValueFactory(AtomParamTypesEnum.STRING, UrlResult),
  create_teammate_list: new ValueFactory(AtomParamTypesEnum.TEAMMATE_LIST, TeammateListResult),
  get_circle: new ValueFactory(AtomParamTypesEnum.NUMBER, CircleResult, {
    format: 'circle',
    featureFlag: FeatureFlagsEnum.CIRCLES,
  }),

  use_previous: new UsePreviousExpression(),
};
